import { Component, Input, Output } from "@angular/core";
import { FormControl, UntypedFormControl } from "@angular/forms";
import { FormType, NGX_SUB_FORM_HANDLE_VALUE_CHANGES_RATE_STRATEGIES, createForm } from "ngx-sub-form";
import { Subject } from "rxjs";
import { EditUserAccount, User } from "./user-model";

@Component({
    selector: 'app-edit-user-form',
    template: `
      <form [formGroup]="form.formGroup">
        <div class="flex flex-col space-y-2">

          @if (isScimUser) {
            <div class="mb-2 p-3 bg-yellow-900/30 border-l-4 border-yellow-400 rounded text-yellow-200 text-sm">
              <i class="pi pi-exclamation-triangle mr-2"></i>
              This user's companies are managed by IT via Active Directory groups. Changes here will not take effect.
            </div>
          }

          <div class="flex flex-col">
            <label>Companies</label>
            <p-multiSelect 
              appendTo="body" 
              [placeholder]="companiesPlaceholder" 
              [options]="companies" 
              [formControlName]="form.formControlNames.companies" 
              optionLabel="name" 
              dataKey="id"
              [disabled]="isScimUser"
            ></p-multiSelect>
            @if (form.formGroup.controls.companies.value?.length === 0 && !isScimUser) {
              <p class="validation-error">Companies are required.</p>
            }
          </div>

          <app-user-profile-form 
            [showAdminInputs]="showAdminInputs"
            [formControlName]="form.formControlNames.profile"
            [user]="user"
          />

          <p-button [disabled]="(!form.formGroup.valid || form.formGroup.controls.companies.value.length === 0) && !isScimUser" (onClick)="save()" label="Save"></p-button>
        </div>

      </form>
    `,
    styles: [``],
    standalone: false
})
export class EditUserFormComponent {

  public isScimUser = false;
  private input$ = new Subject<EditUserAccount | undefined>();
  //This is a fix for the companies dropdown not binding. https://github.com/primefaces/primeng/issues/9673
  public companiesPlaceholder = "";

  constructor() {
    this.companiesPlaceholder = "Select Company";
  }

  @Input() user: User;

  @Input() companies: any[];

  @Input() set userProfile(userProfile: EditUserAccount | undefined) {
    this.input$.next(userProfile);
    // Check if this is a SCIM user by examining the authId
    if (userProfile) {
      const user = userProfile as any;
      this.isScimUser = user.authId?.startsWith('waad|') ?? false;
    }
  }

  private disabled$ = new Subject<boolean>();
  @Input() set disabled(value: boolean | undefined) {
    this.disabled$.next(!!value);
  }

  private _showAdminInputs : boolean;
  @Input() set showAdminInputs(value: boolean) {
    this._showAdminInputs = value;
  }
  get showAdminInputs(): boolean {
    return this._showAdminInputs;
  }

  @Output() userUpdated = new Subject<EditUserAccount>();

  manualSave$ = new Subject<void>();

  public form = createForm<EditUserAccount, EditUserAccount>(this, {
    formType: FormType.ROOT,
    disabled$: this.disabled$,
    input$: this.input$,
    output$: this.userUpdated,
    manualSave$: this.manualSave$,
    formControls: {
      id: new UntypedFormControl(),
      authId: new UntypedFormControl(),
      companies: new FormControl<{ id: string, name: string }[] | null>(null),
      profile: new UntypedFormControl(null)
    },
    handleEmissionRate: NGX_SUB_FORM_HANDLE_VALUE_CHANGES_RATE_STRATEGIES.debounce(1000)
  });

  save() {
    this.manualSave$.next();
  }

}
